import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { RiskFormModal, type RiskFormInitial } from '../features/risks/RiskFormModal';
import { RiskImportModal } from '../features/risks/RiskImportModal';
import { RiskHeatmap } from '../features/risks/RiskHeatmap';
import {
  createRisk,
  downloadRisksExport,
  downloadRisksTemplate,
  fetchRiskMatrix,
  fetchRisks,
} from '../features/risks/risksApi';
import type { RiskEntry, RiskKind, RiskStatus } from '../features/risks/types';
import {
  LEVEL_LABEL_TEXT,
  RISK_KIND_LABEL,
  RISK_STATUS_LABEL,
  levelLabel,
  swotCategoryToRiskKind,
} from '../features/risks/riskUtils';
import type { SwotCategory } from '../features/context/types';

const KINDS: RiskKind[] = ['riesgo', 'oportunidad'];
const STATUSES: RiskStatus[] = [
  'identificado',
  'evaluado',
  'en_tratamiento',
  'monitoreado',
  'cerrado',
];

function parsePrefillFromSearch(params: URLSearchParams): RiskFormInitial | undefined {
  const sourceSwotId = params.get('sourceSwotId');
  const sourcePartyId = params.get('sourcePartyId');
  const kindParam = params.get('kind') as RiskKind | null;
  const swotCategory = params.get('swotCategory') as SwotCategory | null;
  const description = params.get('description') ?? undefined;

  if (!sourceSwotId && !sourcePartyId && !kindParam && !description) return undefined;

  const kind =
    kindParam ??
    (swotCategory ? swotCategoryToRiskKind(swotCategory) : undefined);

  return {
    sourceSwotId: sourceSwotId ?? undefined,
    sourcePartyId: sourcePartyId ?? undefined,
    kind,
    description,
    title: description ? description.slice(0, 120) : undefined,
  };
}

export function RisksPage() {
  const { canEdit } = usePlatformAccessLevel('risks.access');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<RiskEntry[]>([]);
  const [matrixCells, setMatrixCells] = useState<{ probability: number; impact: number; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const prefill = useMemo(() => parsePrefillFromSearch(searchParams), [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listResult, matrixResult] = await Promise.all([
        fetchRisks({
          page,
          pageSize: 20,
          search: search || undefined,
          kind: filterKind || undefined,
          status: filterStatus || undefined,
          sortBy: 'createdAt',
          sortDir: 'desc',
        }),
        fetchRiskMatrix({
          kind: filterKind || undefined,
          status: filterStatus || undefined,
        }),
      ]);
      setItems(listResult.data);
      setTotalPages(listResult.pagination.pages);
      setMatrixCells(matrixResult.cells);
    } catch {
      setError('No se pudieron cargar los riesgos.');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterKind, filterStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('nuevo') === '1' || prefill) {
      setModalOpen(true);
    }
  }, [searchParams, prefill]);

  const handleCreate = async (payload: Parameters<typeof createRisk>[0]) => {
    const created = await createRisk(payload);
    setSearchParams({});
    await load();
    navigate(`/riesgos/${created.id}`);
  };

  const closeModal = (open: boolean) => {
    setModalOpen(open);
    if (!open) setSearchParams({});
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Riesgos y oportunidades</h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Evaluación probabilidad × impacto y plan de tratamiento (ISO 6.1).
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Link
            to='/riesgos/tablero'
            className='px-4 py-2 text-sm rounded-md border hover:bg-muted'
          >
            Tablero MAP
          </Link>
          <button
            type='button'
            onClick={() => void downloadRisksTemplate()}
            className='px-4 py-2 text-sm rounded-md border hover:bg-muted'
          >
            Plantilla FQ-22
          </button>
          <button
            type='button'
            onClick={() => void downloadRisksExport()}
            className='px-4 py-2 text-sm rounded-md border hover:bg-muted'
          >
            Exportar Excel
          </button>
          {canEdit && (
            <>
              <button
                type='button'
                onClick={() => setImportOpen(true)}
                className='px-4 py-2 text-sm rounded-md border hover:bg-muted'
              >
                Importar planilla
              </button>
              <button
                type='button'
                onClick={() => setModalOpen(true)}
                className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground'
              >
                + Nuevo registro
              </button>
            </>
          )}
        </div>
      </div>

      <section className='rounded-lg border p-4 bg-card'>
        <h2 className='text-sm font-semibold mb-3'>Mapa de calor 5×5</h2>
        {loading ? (
          <p className='text-sm text-muted-foreground'>Cargando matriz…</p>
        ) : (
          <RiskHeatmap cells={matrixCells} />
        )}
      </section>

      <div className='flex flex-wrap gap-2'>
        <input
          type='text'
          placeholder='Buscar por código, título…'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className='border rounded-md px-3 py-2 flex-1 min-w-[200px] text-sm'
        />
        <select
          value={filterKind}
          onChange={(e) => {
            setFilterKind(e.target.value);
            setPage(1);
          }}
          className='border rounded-md px-3 py-2 text-sm'
        >
          <option value=''>Todos los tipos</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {RISK_KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className='border rounded-md px-3 py-2 text-sm'
        >
          <option value=''>Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {RISK_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {error && <p className='text-sm text-destructive'>{error}</p>}

      <div className='overflow-x-auto rounded-lg border'>
        <table className='w-full text-sm'>
          <thead className='bg-muted/50'>
            <tr>
              <th className='text-left p-3'>Código</th>
              <th className='text-left p-3'>Título</th>
              <th className='text-left p-3'>Tipo</th>
              <th className='text-left p-3'>Nivel</th>
              <th className='text-left p-3'>Estado</th>
              <th className='text-left p-3'>Sinc.</th>
              <th className='text-left p-3'>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className='p-4 text-muted-foreground'>
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className='p-4 text-muted-foreground'>
                  Sin registros.
                </td>
              </tr>
            )}
            {items.map((risk) => {
              const label = levelLabel(risk.level);
              return (
                <tr key={risk.id} className='border-t'>
                  <td className='p-3 font-mono text-xs'>{risk.code}</td>
                  <td className='p-3'>
                    <Link to={`/riesgos/${risk.id}`} className='text-primary hover:underline'>
                      {risk.title}
                    </Link>
                    {risk.originOutOfSync && (
                      <span className='ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded'>
                        Desactualizado
                      </span>
                    )}
                  </td>
                  <td className='p-3'>{RISK_KIND_LABEL[risk.kind]}</td>
                  <td className='p-3'>
                    {risk.level} ({LEVEL_LABEL_TEXT[label]})
                  </td>
                  <td className='p-3'>{RISK_STATUS_LABEL[risk.status]}</td>
                  <td className='p-3'>
                    {risk.originOutOfSync ? (
                      <span className='text-xs text-amber-700'>Sí</span>
                    ) : (
                      <span className='text-xs text-muted-foreground'>—</span>
                    )}
                  </td>
                  <td className='p-3'>{risk._count?.actions ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className='flex gap-2 justify-end'>
          <button
            type='button'
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className='px-3 py-1 text-sm border rounded disabled:opacity-50'
          >
            Anterior
          </button>
          <span className='text-sm self-center'>
            Página {page} de {totalPages}
          </span>
          <button
            type='button'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className='px-3 py-1 text-sm border rounded disabled:opacity-50'
          >
            Siguiente
          </button>
        </div>
      )}

      {canEdit && (
        <>
          <RiskFormModal
            open={modalOpen}
            onOpenChange={closeModal}
            prefill={prefill}
            onSubmit={handleCreate}
          />
          <RiskImportModal
            open={importOpen}
            onOpenChange={setImportOpen}
            onImported={() => void load()}
          />
        </>
      )}
    </div>
  );
}

export default RisksPage;
